<script>
import { BAlert, BPopover } from 'bootstrap-vue'
import ProductApi from '@core/api/request/product'

const OPEN_EVENT = 'compliance-status-explanation-open'

export default {
    components: {
        BAlert,
        BPopover,
    },
    props: {
        productId: {
            type: [Number, String],
            required: true,
        },
        legislationId: {
            type: [Number, String],
            required: true,
        },
        showDescription: {
            type: Boolean,
            default: false,
        },
        revision: {
            type: Number,
            default: 1,
        },
        closeOnOtherOpen: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            id: `compliance-status-explanation-${(Math.random() + 1).toString(36).substring(7)}`,
            state: 'idle',
            explanation: null,
            error: null,
            popoverVisible: false,
            pendingOpen: false,
        }
    },
    computed: {
        readableFacts() {
            if (!this.explanation || !(this.explanation.facts instanceof Array)) {
                return []
            }

            return this.explanation.facts
                .filter(fact => fact.humanReadable)
                .map(fact => fact.humanReadable)
                .filter(humanReadable => humanReadable.title || humanReadable.description)
        },
    },
    watch: {
        revision() {
            this.reset()
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick)
        window.addEventListener(OPEN_EVENT, this.onOtherOpen)
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocumentClick)
        window.removeEventListener(OPEN_EVENT, this.onOtherOpen)
    },
    methods: {
        reset() {
            this.state = 'idle'
            this.explanation = null
            this.error = null
            this.popoverVisible = false
            this.pendingOpen = false
        },
        load() {
            if (this.state === 'loading' || this.state === 'loaded' || this.state === 'empty') {
                return Promise.resolve()
            }

            if (!this.productId || !this.legislationId) {
                this.state = 'empty'
                return Promise.resolve()
            }

            this.state = 'loading'
            this.error = null

            const requestRevision = this.revision

            return ProductApi.getLegislationStatusExplanation(this.productId, this.legislationId)
                .then(r => {
                    if (requestRevision !== this.revision) {
                        return
                    }

                    this.explanation = r.data
                    this.state = this.readableFacts.length > 0 ? 'loaded' : 'empty'
                })
                .catch(error => {
                    if (requestRevision !== this.revision) {
                        return
                    }

                    this.error = error
                    this.state = 'error'
                })
        },
        open() {
            window.dispatchEvent(new CustomEvent(OPEN_EVENT, {
                detail: {
                    id: this.id,
                },
            }))

            this.pendingOpen = true

            this.load().finally(() => {
                if (this.pendingOpen) {
                    this.popoverVisible = true
                }
            })
        },
        close() {
            this.pendingOpen = false
            this.popoverVisible = false
        },
        toggle() {
            if (this.popoverVisible || this.pendingOpen) {
                this.close()
                return
            }

            this.open()
        },
        onDocumentClick(event) {
            if (!this.popoverVisible && !this.pendingOpen) {
                return
            }

            if (this.$el && this.$el.contains(event.target)) {
                return
            }

            this.close()
        },
        onOtherOpen(event) {
            if (!this.closeOnOtherOpen || !event.detail || event.detail.id === this.id) {
                return
            }

            this.close()
        },
        onKeydown(event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                this.toggle()
            }
        },
        retry() {
            this.state = 'idle'
            this.open()
        },
    },
}
</script>

<template>
    <span>
        <span
            :id="id"
            tabindex="0"
            @keydown="onKeydown"
        >
            <slot
                :state="state"
                :open="open"
                :close="close"
                :toggle="toggle"
                :retry="retry"
            />
        </span>

        <b-popover
            :target="id"
            :show.sync="popoverVisible"
            triggers="manual"
            placement="auto"
        >
            <template #title>
                {{ $t('Component(ComplianceStatusExplanation):title') }}
            </template>

            <div
                v-if="state === 'loading'"
                class="text-muted"
            >
                Loading explanation...
            </div>

            <div v-else-if="state === 'loaded'">
                <div
                    v-for="(fact, index) in readableFacts"
                    :key="index"
                    class="compliance-status-explanation-fact"
                >
                    <strong v-if="fact.title">{{ fact.title }}</strong>
                    <div v-if="showDescription && fact.description">
                        {{ fact.description }}
                    </div>
                </div>
            </div>

            <div
                v-else-if="state === 'empty'"
                class="text-muted"
            >
                {{ $t('Component(ComplianceStatusExplanation):empty') }}
            </div>

            <b-alert
                v-else-if="state === 'error'"
                show
                variant="danger"
                class="mb-0"
            >
                Could not load explanation.
                <button
                    type="button"
                    class="btn btn-link btn-sm p-0 ml-50"
                    @click="retry"
                >
                    Retry
                </button>
            </b-alert>
        </b-popover>
    </span>
</template>

<style scoped lang="scss">
.compliance-status-explanation-fact:not(:last-child) {
    margin-bottom: 0.75rem;
}
</style>
